import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Shield, FileText, Users, Heart, Cookie, Mail, Scale } from 'lucide-react';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white dark:bg-ssdp-navy border-t-4 border-ssdp-teal mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* About Section */}
          <div className="space-y-3">
            <h3 className="text-base font-black text-ssdp-navy dark:text-white font-heading">
              {t('footer.about')}
            </h3>
            <p className="text-sm text-ssdp-navy-light dark:text-gray-300 font-body">
              A global harm reduction platform by SSDP International, empowering youth to make informed decisions.
            </p>
          </div>

          {/* Legal Links */}
          <div className="space-y-3">
            <h3 className="text-base font-black text-ssdp-navy dark:text-white font-heading flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Legal
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/legal/privacy" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body flex items-center gap-2"
                >
                  <Shield className="w-3 h-3" />
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/legal/terms" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body flex items-center gap-2"
                >
                  <FileText className="w-3 h-3" />
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/legal/cookie-notice" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body flex items-center gap-2"
                >
                  <Cookie className="w-3 h-3" />
                  Cookie Notice
                </Link>
              </li>
              <li>
                <Link 
                  to="/legal/disclaimer" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body flex items-center gap-2"
                >
                  <FileText className="w-3 h-3" />
                  Disclaimer
                </Link>
              </li>
            </ul>
          </div>

          {/* Community & Safety */}
          <div className="space-y-3">
            <h3 className="text-base font-black text-ssdp-navy dark:text-white font-heading flex items-center gap-2">
              <Users className="w-4 h-4" />
              Community
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/legal/community-guidelines" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body"
                >
                  Community Guidelines
                </Link>
              </li>
              <li>
                <Link 
                  to="/legal/safeguarding" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body flex items-center gap-2"
                >
                  <Shield className="w-3 h-3" />
                  Safeguarding Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/resources" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body"
                >
                  Support Resources
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="space-y-3">
            <h3 className="text-base font-black text-ssdp-navy dark:text-white font-heading flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Contact
            </h3>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/legal/contact" 
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body"
                >
                  Contact & Transparency
                </Link>
              </li>
              <li>
                <a 
                  href="https://www.ssdp-intl.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-ssdp-navy-light dark:text-gray-300 hover:text-ssdp-teal transition-colors font-body"
                >
                  SSDP International ↗
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-ssdp-navy-light dark:text-gray-400 font-body text-center md:text-left">
            © {currentYear} SSDP International. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {/* TODO: Open cookie preferences */}}
              className="text-sm text-ssdp-navy-light dark:text-gray-400 hover:text-ssdp-teal transition-colors font-body flex items-center gap-1"
              aria-label="Cookie Preferences"
            >
              <Cookie className="w-3 h-3" />
              Cookie Preferences
            </button>
            <span className="text-xs text-gray-400">v1.0.0</span>
          </div>
        </div>

        {/* Emergency Notice */}
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-xl">
          <p className="text-xs text-red-900 dark:text-red-200 font-semibold text-center font-body">
            <Heart className="w-4 h-4 inline mr-2" />
            <strong>Emergency?</strong> Pathways is not an emergency service. 
            Call your local emergency number or crisis hotline for immediate help.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
